/* -------------------------------------------------------------------------------------
Vuex module to manage track data

Main components
---------------
mutations:
- ADD_TRACKHUTS_ELE: 
- ADD_TRACKPARTS_ELE: 
- ADD_TRACKPEAKS_ELE: 
- ADD_TRACKWAYPS_ELE: 
- CREATE_EMPTY_TRACK: 
- SET_TOTAL_TRACKS: 
- SET_TRACK: 
- SET_TRACK_BEGIN_DT: 
- SET_TRACK_BEGINDATETIME: 
- SET_TRACK_FINISHDATETIME: 
- SET_TRACKHUTS: 
- SET_TRACKHUTS_REACHED: 
- SET_TRACKPARTS: 
- SET_TRACKPEAKS: 
- SET_TRACKPEAKS_REACHED: 
- SET_TRACKS: 
- SET_TRACKWAYPS: 
- SET_TRACKWAYPS_REACHED: 

actions:
- createEmptyTrack: Pushes an empty track object to store
- deleteTrack: Deletes current track, trackparts and trackwayps from DB & store
- fetchTracks: Gets all tracks from the API and pushes them to the store
- fetchTrack: Get specific track details from tracks array and push it to vuex store
- insertTrack: Inserts new track via API into DB
- updateTrack: Update the current track in the DB 
- updateStoreTracks: Add or update track in tracks array
- setTrack: push track into vuex store
- addTrackPWEle: Add element to trackparts & trackwayp store
- updateDatetime: Update datetime
- updateReached: Update reached flag

getters:
- getTrackById: Find given track in tracks

TASKS: 
- dispatch('fetchTracks'): can the state be updated instead of re-fetching (see action updateStoreTracks)
- Check why setTrack action is required
- Remove function updateDatetime
- Remove totalTracks (derive from array.length)
---------------------------------------------------------------------------------------*/

// import tourdb library
import ApiService from '@/services/ApiService.js'

export const namespaced = true

export const state = {
  tracks: [],
  track: {},
  totalTracks: 0 // ACTION: Remove totalTracks (derive from array.length)
}

export const mutations = {

  ADD_TRACKHUTS_ELE(state, element) {
    state.track.trackhuts.push(element)
  },

  ADD_TRACKPARTS_ELE(state, element) {
    state.track.trackparts.push(element)
  },

  ADD_TRACKPEAKS_ELE(state, element) {
    state.track.trackpeaks.push(element)
  },

  ADD_TRACKWAYPS_ELE(state, element) {
    state.track.trackwayps.push(element)
  },

  CREATE_EMPTY_TRACK(state, track) {
    state.track = track
  },

  SET_TOTAL_TRACKS(state, totalTracks) {
    state.totalTracks = totalTracks
  },

  SET_TRACKAREAS(state, trackareas) {
    state.track.trackareas = JSON.parse(trackareas)
  },

  SET_TRACKS(state, tracks) {
    state.tracks = tracks
  },

  SET_TRACK(state, track) {
    state.track = track
  },

  SET_TRACK_BEGINDATETIME(state, datetime) {
    state.track.beginDatetime = datetime
  },

  SET_TRACK_FINISHDATETIME(state, datetime) {
    state.track.finishDatetime = datetime
  },

  SET_TRACKHUTS(state, trackhuts) {
    state.track.trackhuts = trackhuts
  },

  SET_TRACKHUTS_REACHED(state, obj) {
    // loop through state object and update reached flag
    let trackhutsNew = []
    state.track.trackhuts.forEach(function(record, index) {
      if (record.id === obj.id) {
        state.track.trackhuts[index].reached = obj.reached
      }
      trackhutsNew.push(record)
    })
    state.track.trackhuts = trackhutsNew
  },

  SET_TRACKPARTS(state, trackparts) {
    state.track.trackparts = trackparts
  },

  SET_TRACKPEAKS_REACHED(state, obj) {
    // loop through state object and update reached flag
    let trackpeaksNew = []
    state.track.trackpeaks.forEach(function(record, index) {
      if (record.id === obj.id) {
        state.track.trackpeaks[index].reached = obj.reached
      }
      trackpeaksNew.push(record)
    })
    state.track.trackpeaks = trackpeaksNew
  },

  SET_TRACKPEAKS(state, trackpeaks) {
    state.track.trackpeaks = trackpeaks
  },

  SET_TRACKWAYPS_REACHED(state, obj) {
    // loop through state object and update reached flag
    let trackwaypsNew = []
    state.track.trackwayps.forEach(function(record, index) {
      if (record.id === obj.id) {
        state.track.trackwayps[index].reached = obj.reached
      }
      trackwaypsNew.push(record)
    })
    state.track.trackwayps = trackwaypsNew
  },

  SET_TRACKWAYPS(state, trackwayps) {
    state.track.trackwayps = trackwayps
  },

  SET_TRACK_BEGIN_DT(beginDate, beginTime) {
    state.track.beginDatetime = beginDate + beginTime
  }
}

export const actions = {
  
  // STANDARD STORE ACTIONS
  // ----------------------

  // Pushes an empty track object to store
  createEmptyTrack({ commit }) {

    let trkEmpty = {
      id: null,
      name: '',
      route: '',
      typeId: null,
      typeCode: '',
      typeName: '',
      subtypeId: null,
      subtypeCode: '',
      subtypeName: '',
      gradeId: null,
      grade: '',
      org: '',
      event: '',
      remarks: '',
      areaId: null,
      areaCode: '',
      areaName: '',
      regionId: null,
      regionCode: '',
      regionName: '',
      countryId: null,
      countryCode: '',
      distance: 0,
      metersUp: 0,
      metersDown: 0,
      beginDatetime: new Date(), //.toISOString().substr(0, 10),
      peakTime: new Date().toLocaleTimeString(),
      lowTime: new Date().toLocaleTimeString(),
      finishDatetime: new Date(), //.toISOString().substr(0, 10),
      startEle: 0,
      peakEle: 0,
      lowEle: 0,
      finishEle: 0,
      trackparts: [],
      trackhuts: [],
      trackpeaks: [],
      trackwayps: []
    }
    commit('CREATE_EMPTY_TRACK', trkEmpty)
  },

  // Deletes current track, trackparts and trackwayps from DB & store
  deleteTrack({ dispatch }) {
    
    ApiService.deleteTrack(this.state.track.track.id)
      .then(() => {
        const notification = {
          type: 'success',
          message: 'The track was successfully deleted'
        }
        dispatch('notification/add', notification, { root: true })

        // after the insert the tracks array is fetched again
        dispatch('fetchTracks')
      })

      // Error handling
      .catch(error => {
        const notification = {
          type: 'error',
          message: 'There was an error deleting the tracks: ' + error.message
        }
        dispatch('notification/add', notification, { root: true })
      })
  },

  // get touched areas for given track
  fetchTouchedAreas({ dispatch, commit }, track) {
    
    ApiService.evalTouchedAreas(track)
      .then(response => {
        commit('SET_TRACKAREAS', response.data.trackareas)
      })

      // Error handling
      .catch(error => {
        const notification = {
          type: 'error',
          message: 'There was an error getting touched areas: ' + error.message
        }
        dispatch('notification/add', notification, { root: true })
      })
  },

  // Gets all tracks from the API and pushes them to the store
  fetchTracks({ commit, dispatch }) {

    let approvingUsers = this.state.user.user.approvingUsers
    let approvedParts = this.state.user.user.approvedParts
    
    ApiService.getTracks(approvingUsers, approvedParts)
      .then(response => {
        commit('SET_TOTAL_TRACKS', response.data.tracks.totalRows)
        commit('SET_TRACKS', response.data.tracks.tracks)
      })

      // Error handling
      .catch(error => {
        const notification = {
          type: 'error',
          message: 'There was an error getting the tracks: ' + error.message
        }
        dispatch('notification/add', notification, { root: true })
      })
  },

  // Get specific track details from tracks array and push it to vuex store
  fetchTrack({ commit, getters }, id) {
   
    var track = getters.getTrackById(id) // See if we already have this track

    commit('SET_TRACK', track) // push track into vuex
  },

  // Inserts new track via API into DB
  insertTrack({ dispatch }) {
    
    ApiService.insertTrack(this.state.track.track)
      .then(() => {
        const notification = {
          type: 'success',
          message: 'The track was successfully inserted'
        }
        dispatch('notification/add', notification, { root: true })

        // after the insert the tracks array is fetched again
        dispatch('fetchTracks')
      })

      // Error handling
      .catch(error => {
        const notification = {
          type: 'error',
          message: 'There was an error inserting the tracks: ' + error.message
        }
        dispatch('notification/add', notification, { root: true })
      })
  },

  // Update the current track in the DB 
  updateTrack({ dispatch }, trackId) {
    
    ApiService.updateTrack(trackId, this.state.track.track)
      .then(() => {
        const notification = {
          type: 'success',
          message: 'The track was successfully saved'
        }
        dispatch('notification/add', notification, { root: true })
      })

      // Error handling
      .catch(error => {
        const notification = {
          type: 'error',
          message: 'There was an error saving the tracks: ' + error.message
        }
        dispatch('notification/add', notification, { root: true })
      })
  },

  // Add or update track in tracks array --> ACTION: Use this instead of re-fetch of tracks
  updateStoreTracks({ commit }, { trackId }) {
    
    let curTracks = JSON.parse(JSON.stringify(this.state.track.tracks))
    let newTracks = []

    if (trackId) {
      // if track id exists it's an update operation

      curTracks.forEach(record => {
        // loop through existing tracks array
      
        if (record.id == trackId) {
          // if current track is the changed track
          newTracks.push(this.state.track.track) // the track is being replaced
        } else {
          newTracks.push(record) // the existing track is pushed out
        }
      })
      
    } else {
      // if no trackId exists, it's an insert operation
      curTracks.push({ track: this.state.track.track })
      newTracks = curTracks
    }
    commit('SET_TRACKS', newTracks)
  },

  // OTHER STORE ACTIONS
  // -------------------

  // push track into vuex store --> ACTION: Check if required / replace with fetchTrack?
  setTrack({ commit }, track) {
    commit('SET_TRACK', track)
  },

  // Add element to trackparts & trackwayp store
  addTrackPWEle({ commit }, { element, eleType }) {
    if (eleType === 'trackparts') {
      commit('ADD_TRACKPARTS_ELE', element)
    } else if (eleType === 'trackhuts') {
      commit('ADD_TRACKHUTS_ELE', element)
    } else if (eleType === 'trackpeaks') {
      commit('ADD_TRACKPEAKS_ELE', element)
    } else if (eleType === 'trackwayps') {
      commit('ADD_TRACKWAYPS_ELE', element)
    }
  },

  // Update datetime --> ACTION: This function should not be required
  updateDatetime({ commit }, { datetime, dateField }) {
    // update the corresponding field
    if (dateField === 'beginDatetime') {
      commit('SET_TRACK_BEGINDATETIME', datetime)
    } else if (dateField === 'finishDatetime') {
      commit('SET_TRACK_FINISHDATETIME', datetime)
    }
  },

  // Update reached flag --> ACTION: Currently not use, but to be reactivated
  updateReached({ commit }, { id, eleType, reached }) {
    // commit corresponding mutation if reached flag is changed
    if (eleType === 'hut') {
      commit('SET_TRACKHUTS_REACHED', { id, reached })
    } else if (eleType === 'peak') {
      commit('SET_TRACKPEAKS_REACHED', { id, reached })
    } else if (eleType === 'wayp') {
      commit('SET_TRACKWAYPS_REACHED', { id, reached })
    }
  },

  /*
  updateTrackArrays({ commit }, { id, type }) {
    // remove deleted element with id from array
    let newArr = this.state.track.track[type].filter(record => {
      return record.id !== id
    })

    this.state.track.track[type] = newArr
    if (type === 'trackparts') {
      commit('SET_TRACKPARTS', newArr)
    } else if (type === 'trackhuts') {
      commit('SET_TRACKHUTS', newArr)
    } else if (type === 'trackpeaks') {
      commit('SET_TRACKPEAKS', newArr)
    } else if (type === 'trackwayps') {
      commit('SET_TRACKWAYPS', newArr)
    }
  }*/
}

export const getters = {

  // find given track in tracks
  getTrackById: state => id => {
    let tracks = JSON.parse(JSON.stringify(state))
    return tracks.tracks.find(track => track.id === id.id)
  }
}
